/**
 * H2 — the one place `Sentry.init`'s options are decided.
 *
 * Three entry points call this: `src/instrumentation-client.ts` (browser),
 * `src/sentry.server.config.ts` (Node) and `src/sentry.edge.config.ts` (the
 * edge runtime `middleware.ts` runs on). They differ only in which SDK build
 * the import resolves to; the options are identical, which is the point —
 * a scrub rule that applied to two runtimes out of three would be a hole.
 *
 * Framework-free apart from the type import. No `Sentry.init` call lives here,
 * so the whole module is loadable in vitest without the SDK.
 *
 * ⚠️ `NEXT_PUBLIC_*` is inlined by source-level substitution at BUILD time, so
 * every read below is a literal `process.env['NEXT_PUBLIC_…']`. A computed or
 * destructured read is not substituted and silently becomes `undefined` in the
 * deployed bundle while working perfectly in `next dev`. Same shape, same
 * reason, as `api-url.ts` and `client-version.ts`.
 */
import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import { APP_VERSION } from './client-version';
import { sentryBeforeBreadcrumb, sentryBeforeSend } from './sentry-filter';

/**
 * Committed on purpose, exactly like `NEXT_PUBLIC_API_URL`'s default.
 *
 * A DSN is a write-only address: Sentry's own documentation says DSNs "are
 * safe to keep public because they only allow submission of new events and
 * related event data; they do not allow read access to any information". The
 * worst it enables is somebody posting junk events at our quota, which is
 * fixed by rotating the DSN. Keeping it in a repo variable instead would buy
 * no secrecy (it ships inside the JavaScript bundle regardless) and would add
 * a fifth thing that must be set for a build to be correct.
 *
 * This is the EU-region (Frankfurt) ingest host — note `ingest.de.sentry.io`.
 * The source-map upload in `next.config.mjs` must point at the matching region
 * API (`https://de.sentry.io`) or it authenticates against the wrong one.
 */
const DEFAULT_DSN =
  'https://59f4d396a9fa8469805c1c2230976147@o4511970922594304.ingest.de.sentry.io/4511970934980688';

/**
 * Setting `NEXT_PUBLIC_SENTRY_DSN=''` at build time is the OFF switch: it
 * overrides the default with an empty string, `sentryEnabled()` goes false,
 * and no entry point ever calls `Sentry.init`. That is what keeps Sentry
 * optional rather than a dependency of the build.
 */
export const SENTRY_DSN: string =
  typeof process !== 'undefined'
    ? (process.env['NEXT_PUBLIC_SENTRY_DSN'] ?? DEFAULT_DSN)
    : DEFAULT_DSN;

/**
 * Which deployment an event came from. Only `dev` exists today — there are no
 * production hostnames yet — so that is the honest default rather than
 * 'production'. `deploy-web.yml` passes it explicitly all the same, so the day
 * a second environment appears the value comes from the workflow and not from
 * this line.
 */
export const SENTRY_ENVIRONMENT: string =
  typeof process !== 'undefined' ? (process.env['NEXT_PUBLIC_SENTRY_ENVIRONMENT'] ?? 'dev') : 'dev';

/**
 * The release, reusing H1's build version — the short git SHA of the commit
 * the image was built from. NOT a second version mechanism: this is the same
 * `NEXT_PUBLIC_BUILD_VERSION` the `X-App-Version` header sends.
 *
 * ⚠️ `next.config.mjs` sets the build plugin's `release.name` from the SAME
 * env var. Those two values must be identical or source maps upload against a
 * release no event ever references, and every stack trace stays minified while
 * the build log says the upload succeeded. `sentry.test.ts` asserts both read
 * the same variable name.
 */
export const SENTRY_RELEASE: string = APP_VERSION;

/**
 * Sentry is on only when there is a DSN AND this is a production build.
 *
 * The `NODE_ENV` half is not belt-and-braces. Next bakes `NODE_ENV` at build
 * time: `next dev` is 'development' and vitest is 'test', so a local session
 * and the test suite are off by construction and cannot spend the 5,000-event
 * monthly quota or ship a developer's stack trace to a shared dashboard.
 *
 * Arguments are injectable so the decision itself is testable; the defaults
 * are the real build-time values.
 */
export function sentryEnabled(
  dsn: string = SENTRY_DSN,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): boolean {
  return dsn.trim() !== '' && nodeEnv === 'production';
}

/** Shape of what `Sentry.init` receives — kept explicit so tests can assert it. */
export interface SentryInitOptions {
  dsn: string;
  release: string;
  environment: string;
  sendDefaultPii: false;
  tracesSampleRate: 0;
  maxBreadcrumbs: number;
  // Typed with `ErrorEvent` (an `Event` narrowed to `type: undefined`), which is
  // what `Sentry.init` requires; `sentryBeforeSend` is generic and instantiates
  // to it. Widening this to `Event` makes `Sentry.init(...)` stop typechecking.
  beforeSend: (event: ErrorEvent, hint?: EventHint) => ErrorEvent | null;
  beforeBreadcrumb: typeof sentryBeforeBreadcrumb;
}

/**
 * `sendDefaultPii: false` is the SDK default and is stated anyway — it is the
 * switch that decides whether IP addresses, cookies and request bodies are
 * attached automatically, and it must never be flipped on this codebase.
 *
 * `tracesSampleRate: 0` turns performance tracing off, and no replay
 * integration is registered, so neither is ever sent. Both share the same free
 * plan as errors; a trace budget spent is a crash not reported. Sentry's
 * performance monitoring is explicitly out of scope for H2 (see the tranche's
 * own "Cut" note about per-procedure timing).
 */
export function sentryInitOptions(): SentryInitOptions {
  return {
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    // Capped low on purpose: every breadcrumb is one more thing to scrub, and
    // the useful ones are the last handful before the crash.
    maxBreadcrumbs: 30,
    beforeSend: sentryBeforeSend,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
  };
}
